import { HSConversations, HSWidget } from './utils/types'

import { appendScript } from '@/helpers/common'
import { getHubspotScriptSrc } from './utils/helpers'

const SCRIPT_ID = 'hs-script-loader'

let isReady = false
let isLoaded = false
let scriptId: string | undefined

export const useHubspot = (hubspotScriptId = scriptId) => {

  scriptId = hubspotScriptId
  if (!hubspotScriptId) return

  const getHS = (): HSConversations | undefined => window.HubSpotConversations
  const getWidget = (): HSWidget | undefined => getHS()?.widget

  const widget = new Promise<HSWidget>(resolve => {
    const interval = setInterval(() => {
      const w = getWidget()
      if (w) {
        clearInterval(interval)
        resolve(w)
      }
    }, 100)
  })

  const conversations = new Promise<HSConversations>(resolve => {
    const interval = setInterval(() => {
      const c = getHS()
      if (c) {
        clearInterval(interval)
        resolve(c)
      }
    }, 100)
  })

  const init = async (
    identificationEmail?: string,
    identificationToken?: string,
    loadImmediately = true
  ) => {
    const src = getHubspotScriptSrc(hubspotScriptId)
    if (!src) return

    window.hsConversationsSettings = {
      loadImmediately,
      identificationEmail,
      identificationToken,
    }
    await appendScript(src, SCRIPT_ID, {
      async: 'true',
      defer: 'true'
    }, true)
    isReady = true
    if (!loadImmediately) return
    load()
    isLoaded = true
  }

  const status = () => widget.then(w => w.status?.())

  const open = () => {
    if (isLoaded) {
      widget.then(w => w.open())
    } else {
      const open = window.hsConversationsSettings?.loadImmediately
      load(open ? undefined : { widgetOpen: true })
    }
  }

  const close = () => {
    if (!isLoaded) return
    widget.then(w => w.close())
  }

  const destroy = () => {
    if (!isLoaded) return
    window.hsConversationsSettings = undefined
    isLoaded = false
    widget.then(w => {
      w.remove()
      document.getElementById(SCRIPT_ID)?.remove()
    })
  }

  const load = (params?: Record<string, unknown>) => {
    widget.then(w => {
      w.load(params)
      isLoaded = true
    })
  }

  const refresh = () => {
    widget.then(w => w.refresh())
  }

  const reset = () => {
    conversations.then(c => c?.resetAndReloadWidget?.())
  }

  const on = (event: string, handler: (...args: any[]) => void) => {
    conversations.then(c => c?.on?.(event, handler))
  }

  const off = (event: string, handler: (...args: any[]) => void) => {
    conversations.then(c => c?.off?.(event, handler))
  }

  return {
    init,
    status,
    isReady,
    isLoaded,

    open,
    close,
    destroy,
    load,
    refresh,
    reset,

    on,
    off,
  }
}
